<template>
  <Modal
    :is-open="isOpen"
    size="md"
    :close-on-backdrop="false"
    :show-close-button="false"
    container-class="max-w-md"
    @close="handleClose"
    :scrollable="false"
  >
    <div class="flex flex-col items-center justify-center">
      <div
        :class="[
          'w-16 h-16 rounded-full flex items-center justify-center mb-4',
          storeStatus === 'closed' ? 'bg-red-50' : 'bg-yellow-50'
        ]"
      >
        <AlertCircle
          :size="40"
          :class="storeStatus === 'closed' ? 'text-red-600' : 'text-yellow-600'"
        />
      </div>

      <h4 class="text-2xl font-serif font-bold text-gray-900 mb-2 text-center">
        {{ modalTitle }}
      </h4>

      <p class="text-gray-600 text-center text-sm mb-6" v-html="mainMessage" />

      <div
        v-if="workingHours && workingHours.dayLabel && workingHours.openingTime"
        class="w-full mt-4 pt-4 border-t border-gray-100"
      >
        <p class="text-base font-medium text-gray-900 text-center">
          Reopens {{ workingHours.dayLabel }} at {{ workingHours.openingTime }}
        </p>
      </div>

      <div class="w-full mt-6 space-y-3">
        <template v-if="canScheduleOrder">
          <button
            @click="handleClose"
            class="w-full bg-primary-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
          >
            Schedule my Order
          </button>

          <button
            v-if="canChangeLocation"
            @click="handleChangeLocation"
            class="w-full text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-100 transition-all"
          >
            Change Location
          </button>
        </template>

        <template v-else>
          <button
            v-if="canChangeLocation"
            @click="handleChangeLocation"
            class="w-full bg-primary-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
          >
            Select a Different Location
          </button>
          <button
            v-else
            @click="handleClose"
            class="w-full bg-primary-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
          >
            Close
          </button>
        </template>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "../ui/Modal.vue";
import { AlertCircle } from "lucide-vue";
import { useSelectedStore } from "@/composables/useSelectedStore";
import { useModalManagement } from "@/composables/useModalManagement";
import moment from "moment-timezone";

export default {
  name: "LoactionIsClosedModal",
  components: {
    Modal,
    AlertCircle
  },
  props: {},
  created() {
    this._selectedStore = useSelectedStore(this.$store);
    this._modalManagement = useModalManagement(this.$store);
  },
  mounted() {
    this._modalManagement.setRestaurantClosedModalOpen(true);
  },

  computed: {
    storeStatus() {
      if (this._selectedStore) {
        return this._selectedStore.getStoreStatus();
      }
      return "open";
    },
    shouldShowModal() {
      // Show modal if status is 'closed' or 'schedule_only'
      return (
        this.storeStatus === "closed" || this.storeStatus === "schedule_only"
      );
    },
    isOpen() {
      // Only show if modal should be visible and modal management says it's open
      return (
        this.shouldShowModal &&
        this._modalManagement.getIsRestaurantClosedModalOpen()
      );
    },
    nextOpenDayInfo() {
      if (this._selectedStore) {
        // If store is closed, show next open day instead of current hours
        if (
          this.storeStatus === "closed" ||
          this.storeStatus === "schedule_only"
        ) {
          return this._selectedStore.getNextOpenDay();
        }
      }
      return null;
    },
    workingHours() {
      if (this._selectedStore) {
        // If store is closed, show next open day instead of current hours
        if (
          this.storeStatus === "closed" ||
          this.storeStatus === "schedule_only"
        ) {
          const nextOpenDay = this.nextOpenDayInfo;
          if (nextOpenDay) {
            // Format the day label: Today, Tomorrow, or day name
            let dayLabel = "";
            if (nextOpenDay.isToday) {
              dayLabel = "Today";
            } else if (nextOpenDay.isTomorrow) {
              dayLabel = "Tomorrow";
            } else {
              dayLabel = nextOpenDay.dayName;
            }

            // Get opening time
            const openingTime = nextOpenDay.openingTime || "";

            return {
              dayLabel: dayLabel,
              dayDate: nextOpenDay.dayDate,
              hours: nextOpenDay.hours,
              openingTime: openingTime,
              isToday: nextOpenDay.isToday,
              isTomorrow: nextOpenDay.isTomorrow
            };
          }
        }
        // Otherwise, show current working hours
        const currentHours = this._selectedStore.getStoreWorkingHours();
        return {
          dayLabel: null,
          dayDate: null,
          hours: currentHours,
          openingTime: null,
          isToday: false,
          isTomorrow: false
        };
      }
      return {
        dayLabel: null,
        dayDate: null,
        hours: "",
        openingTime: null,
        isToday: false,
        isTomorrow: false
      };
    },
    canScheduleOrder() {
      if (this._selectedStore) {
        return this._selectedStore.canScheduleOrder();
      }
      return false;
    },
    /**
     * Whether the user should be offered the option to change location.
     * Only relevant when there is more than one location and at least one
     * other location is available.
     * @returns {boolean}
     */
    canChangeLocation() {
      const allStores = this.$store.getters.getStores || [];
      return allStores.length > 1 && this.hasAvailableStores;
    },
    /**
     * Checks if there are any stores available (open or schedule_only)
     * @returns {boolean} True if there are available stores
     */
    hasAvailableStores() {
      const allStores = this.$store.getters.getStores || [];
      const allDiningOptions = this.$store.getters.availableDiningOptions || [];
      const now = moment();

      // Check if at least one store is open or schedule_only
      return allStores.some((store) => {
        const timezone =
          store.timezone || this.$store.getters.getStoreTimezone || "UTC";
        const nowInTimezone = now.clone().tz(timezone);
        const todayDay = nowInTimezone.isoWeekday();

        // Use the same logic as calculateStoreStatusOnce
        let hasOpenOption = false;
        let hasScheduleOption = false;
        let hasAnyTimingForToday = false;
        let allTimingsClosed = true;

        for (const option of allDiningOptions) {
          if (option.is_scheduling_enabled === true) {
            hasScheduleOption = true;
          }

          if (!option.timings || option.timings.length === 0) {
            continue;
          }

          const allTimingsForToday = option.timings.filter(
            (timing) => timing.day_of_week === todayDay
          );

          if (allTimingsForToday.length === 0) {
            continue;
          }

          hasAnyTimingForToday = true;

          const allClosedForThisOption = allTimingsForToday.every(
            (timing) => timing.is_closed === true
          );

          if (allClosedForThisOption) {
            continue;
          } else {
            allTimingsClosed = false;
          }

          const timingsForToday = allTimingsForToday.filter(
            (timing) => timing.is_closed === false
          );

          if (timingsForToday.length === 0) {
            continue;
          }

          const isOpen = timingsForToday.some((timing) => {
            const today = nowInTimezone.clone().startOf("day");
            const openTime = today
              .clone()
              .add(moment.duration(timing.opening_time));
            const closeTime = today
              .clone()
              .add(moment.duration(timing.closing_time));

            if (closeTime.isBefore(openTime) || closeTime.isSame(openTime)) {
              return (
                nowInTimezone.isSameOrAfter(openTime) ||
                nowInTimezone.isBefore(closeTime)
              );
            }

            const nextMinuteAfterClose = closeTime.clone().add(1, "minute");
            const isWithinHours =
              nowInTimezone.isSameOrAfter(openTime) &&
              nowInTimezone.isBefore(nextMinuteAfterClose);
            return isWithinHours;
          });

          if (isOpen) {
            hasOpenOption = true;
          }
        }

        // Store is available if it's open or schedule_only
        if (hasOpenOption) {
          return true;
        }

        if (hasAnyTimingForToday && allTimingsClosed) {
          if (hasScheduleOption) {
            return true; // schedule_only
          }
          return false; // closed
        }

        if (hasScheduleOption) {
          return true; // schedule_only
        }

        return false; // closed
      });
    },
    modalTitle() {
      if (this.storeStatus === "schedule_only") {
        return "Location Closed - Schedule Available";
      }
      return "Location Closed";
    },
    mainMessage() {
      if (this.storeStatus === "schedule_only") {
        if (this.canChangeLocation) {
          return "The selected location is <b>currently closed</b>.<br>You can schedule your order for a later time or switch to a different location.";
        }
        return "The selected location is <b>currently closed</b>.<br>You can schedule your order for a later time.";
      } else {
        // If there's no other location to switch to, don't mention changing location
        if (!this.canChangeLocation) {
          return "The selected location is <b>currently closed</b> and does not accept scheduled orders. Please check back later.";
        }
        return "The selected location is <b>currently closed</b> and does not accept scheduled orders. Please select a different location to proceed.";
      }
    }
  },
  methods: {
    handleClose() {
      this._modalManagement.setRestaurantClosedModalOpen(false);
    },
    handleChangeLocation() {
      this._modalManagement.setRestaurantClosedModalOpen(false);
      this._modalManagement.setLocationModalOpen(true);
    }
  }
};
</script>
